/**
 * Lead List Table Component
 * 
 * Material-UI DataGrid with server-side pagination, sorting, and inline actions
 * 
 * Requirements: 9.5, 11.4, 11.5, 15.1, 15.2
 * Task: 21.1
 */

import React from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem, GridRowParams, GridSortModel } from '@mui/x-data-grid';
import { Lead, LeadStatus, LeadSource } from '../../types/lead';
import GSTVerificationBadge from './GSTVerificationBadge';

interface LeadListTableProps {
  leads: Lead[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  onEdit: (leadId: string) => void;
  onDelete: (leadId: string) => void;
  onView: (leadId: string) => void;
}

/**
 * LeadListTable Component
 * 
 * Displays leads in a Material-UI DataGrid with:
 * - Server-side pagination
 * - Sortable columns (name, company, phone, status, source, created_at)
 * - Inline actions (view, edit, delete)
 * - GST verification status badge
 */
const LeadListTable: React.FC<LeadListTableProps> = ({
  leads,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onEdit,
  onDelete,
  onView,
}) => {
  // Status color mapping
  const getStatusColor = (status: LeadStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
    switch (status) {
      case LeadStatus.NEW:
        return 'info';
      case LeadStatus.CONTACTED:
        return 'default';
      case LeadStatus.QUALIFIED:
        return 'warning';
      case LeadStatus.PROPOSAL_SENT:
        return 'primary';
      case LeadStatus.NEGOTIATION:
        return 'warning';
      case LeadStatus.CONVERTED:
        return 'success';
      case LeadStatus.LOST:
        return 'error';
      default:
        return 'default';
    }
  };

  // Status label mapping
  const getStatusLabel = (status: LeadStatus): string => {
    switch (status) {
      case LeadStatus.NEW:
        return 'New Inquiry';
      case LeadStatus.CONTACTED:
        return 'Contacted';
      case LeadStatus.QUALIFIED:
        return 'Qualified';
      case LeadStatus.PROPOSAL_SENT:
        return 'Proposal Sent';
      case LeadStatus.NEGOTIATION:
        return 'Negotiating';
      case LeadStatus.CONVERTED:
        return 'Converted';
      case LeadStatus.LOST:
        return 'Lost';
      default:
        return status;
    }
  };

  // Source badge renderer with rich aesthetics
  const renderSourceBadge = (source: string) => {
    const s = (source || '').toLowerCase();
    if (s.includes('indiamart')) {
      return (
        <Chip
          size="small"
          label="IndiaMART"
          sx={{
            bgcolor: '#FFF3E0',
            color: '#E65100',
            fontWeight: 700,
            fontSize: '0.75rem',
            border: '1px solid #FFE0B2',
          }}
        />
      );
    }
    if (s.includes('facebook') || s.includes('meta')) {
      return (
        <Chip
          size="small"
          label="Meta / FB Ads"
          sx={{
            bgcolor: '#E7F3FF',
            color: '#1877F2',
            fontWeight: 700,
            fontSize: '0.75rem',
            border: '1px solid #BBDEFB',
          }}
        />
      );
    }
    if (s.includes('instagram')) {
      return (
        <Chip
          size="small"
          label="Instagram"
          sx={{
            bgcolor: '#FCE4EC',
            color: '#D81B60',
            fontWeight: 700,
            fontSize: '0.75rem',
            border: '1px solid #F8BBD0',
          }}
        />
      );
    }
    if (s.includes('website')) {
      return (
        <Chip
          size="small"
          label="Website"
          sx={{
            bgcolor: '#E0F2F1',
            color: '#00796B',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      );
    }
    if (s.includes('referral')) {
      return (
        <Chip
          size="small"
          label="Referral"
          sx={{
            bgcolor: '#EDE7F6',
            color: '#512DA8',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label={source.replace('_', ' ')}
        sx={{
          bgcolor: '#F5F5F5',
          color: '#616161',
          fontWeight: 500,
          fontSize: '0.75rem',
          textTransform: 'capitalize',
        }}
      />
    );
  };

  // Lead Type badge renderer
  const renderLeadTypeBadge = (leadType?: string | null) => {
    if (!leadType) return null;
    const typeColors: Record<string, { bg: string; color: string }> = {
      wholesaler: { bg: '#F3E5F5', color: '#7B1FA2' },
      distributor: { bg: '#E8EAF6', color: '#283593' },
      retailer: { bg: '#E1F5FE', color: '#0277BD' },
      epc_contractor: { bg: '#FFF8E1', color: '#F57F17' },
      corporate: { bg: '#E8F5E9', color: '#2E7D32' },
      individual: { bg: '#F5F5F5', color: '#616161' },
    };
    const style = typeColors[leadType.toLowerCase()] || { bg: '#F5F5F5', color: '#424242' };
    return (
      <Chip
        size="small"
        label={leadType.replace('_', ' ')}
        sx={{
          bgcolor: style.bg,
          color: style.color,
          fontWeight: 600,
          fontSize: '0.7rem',
          height: '20px',
          textTransform: 'capitalize',
        }}
      />
    );
  };

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Lead / Buyer Profile',
      flex: 1.2,
      minWidth: 260,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <Typography 
              variant="body2" 
              fontWeight={700} 
              sx={{ 
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              {params.row.name}
            </Typography>
            <GSTVerificationBadge
              status={params.row.gst_verification_status}
              verifiedAt={params.row.gst_verified_at}
              iconOnly
            />
            {renderLeadTypeBadge(params.row.lead_type)}
          </Box>
          {params.row.company && (
            <Typography 
              variant="body2" 
              sx={{ 
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.8125rem',
                fontWeight: 500,
                lineHeight: 1.3,
              }}
            >
              {params.row.company}
            </Typography>
          )}
          {params.row.email && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                color: 'text.disabled',
                fontSize: '0.75rem',
              }}
            >
              {params.row.email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Contact Phone',
      width: 135,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary', letterSpacing: 0.5 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'source',
      headerName: 'Channel / Source',
      width: 155,
      sortable: true,
      renderCell: (params) => renderSourceBadge(params.value),
    },
    {
      field: 'location',
      headerName: 'Location / Zone',
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const { city, state, region } = params.row;
        const locationText = [city, state].filter(Boolean).join(', ') || params.row.location || '-';
        return (
          <Box>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {locationText}
            </Typography>
            {region && (
              <Chip
                size="small"
                label={`${region} Zone`}
                sx={{
                  bgcolor: '#E3F2FD',
                  color: '#1565C0',
                  fontSize: '0.65rem',
                  height: '18px',
                  fontWeight: 600,
                  mt: 0.25,
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'interest_category',
      headerName: 'Requirement / Query',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
          {params.value || params.row.query || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Pipeline Stage',
      width: 140,
      sortable: true,
      renderCell: (params) => (
        <Chip
          size="small"
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Generated On',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="View">
              <ViewIcon />
            </Tooltip>
          }
          label="View"
          onClick={() => onView(params.row.id)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Edit">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          onClick={() => onEdit(params.row.id)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Delete">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          onClick={() => onDelete(params.row.id)}
          showInMenu
        />,
      ],
    },
  ];

  // Handle sort model change
  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      if (sort) {
        onSortChange(field, sort as 'asc' | 'desc');
      }
    }
  };

  // Handle pagination model change
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    // DataGrid uses 0-indexed pages, but our API uses 1-indexed
    // So we need to add 1 when calling onPageChange
    if (model.page !== page) {
      onPageChange(model.page);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={leads}
        columns={columns}
        loading={loading}
        
        // Pagination configuration
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        rowCount={totalCount}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 20, 50, 100]}
        
        // Sorting configuration
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        
        // Row configuration
        disableRowSelectionOnClick
        autoHeight
        getRowHeight={() => 'auto'}
        
        // Hide footer if no data
        hideFooter={leads.length === 0 && !loading}
        
        sx={{
          minHeight: 400,
          '& .MuiDataGrid-cell': {
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row': {
            minHeight: '80px !important',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
            bgcolor: 'action.hover',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '2px solid',
            borderColor: 'divider',
            minHeight: '56px !important',
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            backgroundColor: 'background.paper',
          },
          '& .MuiTablePagination-root': {
            overflow: 'visible',
            display: 'flex',
          },
          '& .MuiTablePagination-toolbar': {
            minHeight: '56px',
            paddingLeft: 2,
            paddingRight: 2,
          },
          '& .MuiTablePagination-actions': {
            marginLeft: 2,
            display: 'flex',
            gap: 0.5,
          },
          '& .MuiTablePagination-actions button': {
            padding: 1,
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
            fontSize: '0.875rem',
          },
          '& .MuiTablePagination-selectLabel': {
            margin: 0,
            fontSize: '0.875rem',
          },
        }}
      />
    </Box>
  );
};

export default LeadListTable;
